import { defineStore } from "pinia";
import {
  adminEfpLogin,
  adminEfpValidate,
  adminEfpLogout,
  adminEfpAutocompleteVendeurs,
  adminEfpAutocompleteAcheteurs,
  adminEfpGetVendeurDetails,
  adminEfpGetAcheteurDetails,
  adminEfpGetCustomerAuthUrl,
  adminEfpImpersonate,
  type AdminEfpVendeur,
  type AdminEfpVendeurDetails,
  type AdminEfpAcheteur,
  type AdminEfpAcheteurDetails,
  type AdminEfpAdmin,
} from "@/api/adminEfp";
import {
  ADMIN_MODULES,
  ADMIN_PERMISSION_ROUTES,
  permissionForAdminPath,
  type AdminPermissionKey,
} from "@/config/adminPermissions";

export const useAdminEfpStore = defineStore("adminEfp", {
  state: () => ({
    validated: false,
    admin: null as AdminEfpAdmin | null,
  }),

  getters: {
    isAdminEfpValidated: (state) => state.validated,
    adminDisplayName: (state): string => {
      const a = state.admin;
      if (!a) return "";
      const parts = [a.prenom, a.nom].filter(Boolean);
      return parts.length ? parts.join(" ") : a.email;
    },
    isSuperAdmin: (state): boolean => state.admin?.isSuperAdmin === true,
    permissions: (state): string[] => state.admin?.permissions ?? [],
  },

  actions: {
    canAccess(permission: AdminPermissionKey): boolean {
      return (
        this.admin?.isSuperAdmin === true ||
        (this.admin?.permissions ?? []).includes(permission)
      );
    },

    canAccessPath(path: string): boolean {
      if (path.startsWith("/adminEFP/access")) return this.isSuperAdmin;
      if (path.startsWith("/adminEFP/logging")) return this.isSuperAdmin;
      const permission = permissionForAdminPath(path);
      return permission === null || this.canAccess(permission);
    },

    firstAccessiblePath(): string {
      if (this.isSuperAdmin) return ADMIN_PERMISSION_ROUTES.dashboard;
      const first = ADMIN_MODULES.find(
        (module) =>
          module.key !== "edit-siret-tva" && module.key !== "groupages-change-status" && this.canAccess(module.key)
      );
      return first ? ADMIN_PERMISSION_ROUTES[first.key] : "/adminEFP/no-access";
    },
    async login(email: string, password: string) {
      const res = await adminEfpLogin(email, password);
      this.validated = true;
      this.admin = res.admin ?? null;
    },

    /** Vérifie la session via cookie HttpOnly. Retourne true si ok, false si 401. */
    async validate(): Promise<boolean> {
      try {
        const res = await adminEfpValidate();
        this.validated = true;
        this.admin = res.admin ?? null;
        return true;
      } catch {
        this.validated = false;
        this.admin = null;
        return false;
      }
    },

    async logout() {
      try {
        await adminEfpLogout();
      } catch {
        /* ignorer */
      }
      this.validated = false;
      this.admin = null;
    },

    async autocompleteVendeurs(term: string): Promise<AdminEfpVendeur[]> {
      return adminEfpAutocompleteVendeurs(term);
    },

    async autocompleteAcheteurs(term: string): Promise<AdminEfpAcheteur[]> {
      return adminEfpAutocompleteAcheteurs(term);
    },

    async getVendeurDetails(id: number): Promise<AdminEfpVendeurDetails> {
      return adminEfpGetVendeurDetails(id);
    },

    async getAcheteurDetails(id: number): Promise<AdminEfpAcheteurDetails> {
      return adminEfpGetAcheteurDetails(id);
    },

    async getCustomerAuthUrl(id: number): Promise<string> {
      return adminEfpGetCustomerAuthUrl(id);
    },

    async impersonate(
      sellerId: number
    ): Promise<{ ok: boolean; adminDisplayName: string | null }> {
      return adminEfpImpersonate(sellerId);
    },
  },
});
